// Session context. Bootstraps from GET /api/auth/me, exposes the current user +
// active company + memberships, and the actions that change session state.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api } from "./api";
import type { Me } from "./types";

interface SessionContextValue {
  me: Me | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMe(await api.auth.me());
    } catch {
      setError("session");
      setMe({ user: null, activeCompany: null, companies: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.auth.logout();
    await refresh();
  }, [refresh]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      await api.auth.switchCompany(companyId);
      await refresh();
    },
    [refresh],
  );

  return (
    <SessionContext.Provider value={{ me, loading, error, refresh, logout, switchCompany }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
